/**
 * THE one credentialed fetch path to the api host, shared by both SPAs.
 *
 * The dashboard and the platform-admin console each carried their own copy of this file. They
 * were 95% identical and had already drifted: only one of them redirected a 402, and the two
 * hung the failed response body off differently-named fields. Two copies of a transport that
 * both sign requests is exactly the code that must not drift, so the mechanism lives here once
 * and each app keeps a thin binding that names its own token store and its own error class.
 *
 * Every call site goes through this so no call site can forget the two load-bearing primitives
 * (the exact Phase-12 cross-subdomain CORS regression — Pitfall 2, which curl cannot catch but a
 * real browser preflight does):
 *
 *   1. `credentials: 'include'`  — send/receive the qelm_session + qelm_csrf cookies.
 *   2. `X-Tenant-Subdomain: <tenant>` — the ONLY tenant signal the api host has (the api host
 *      carries no tenant in its own hostname). The platform-admin host has no tenant of its own,
 *      so the value is empty there — the `/admin/api/*` gate is admin-cookie driven and ignores it.
 *
 * On a 401 we run a SINGLE-FLIGHT refresh (one in-flight refresh shared by all callers), then
 * retry ONCE. A refused refresh is the sign-out path: no retry, the 401 goes back to the caller.
 * Mutations attach the double-submit `X-CSRF-Token` read from the readable `qelm_csrf` cookie.
 *
 * The api host is NEVER hardcoded here — it is injected, because each app reads it from
 * `@hbs-us/contracts` `getEnvConfig(env).domains.api` off its own host (RESEARCH anti-pattern,
 * threat T-20-07: the SPA ships no secret and reads only the public host).
 */
const CSRF_COOKIE = 'qelm_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
/**
 * Pick the `qelm_csrf` cookie bound to `expectedHost`.
 *
 * The browser holds one per host, `document.cookie` returns them in an order the page does not
 * control, and taking the FIRST match sends the wrong one — which the server rejects as a 400
 * with a plain-text body the SPA cannot parse, so it surfaces as "check your connection".
 */
export function selectCsrfToken(cookieHeader, expectedHost) {
    for (const row of cookieHeader.split('; ')) {
        if (!row.startsWith(`${CSRF_COOKIE}=`))
            continue;
        const token = decodeURIComponent(row.slice(CSRF_COOKIE.length + 1));
        try {
            const payload = token.split('.')[0] ?? '';
            const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
            const decoded = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
            if (decoded.h === expectedHost)
                return token;
        }
        catch {
            // The server verifies the signature; malformed candidates are unusable here.
        }
    }
    return null;
}
/** Build the transport for one app. Each app creates exactly one. */
export function createApiTransport(config) {
    // Single-flight refresh: all 401s during one refresh share the same promise.
    let refreshInFlight = null;
    let refreshImpl = null;
    function runSingleFlightRefresh() {
        if (!refreshImpl)
            return Promise.resolve(false);
        if (!refreshInFlight) {
            refreshInFlight = refreshImpl().finally(() => {
                refreshInFlight = null;
            });
        }
        return refreshInFlight;
    }
    function buildHeaders(method, init) {
        const headers = new Headers(init);
        // The ONLY tenant signal to the api host — bake it in for every call.
        headers.set('X-Tenant-Subdomain', config.getTenantSubdomain());
        const accessToken = config.getAccessToken();
        if (accessToken && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${accessToken}`);
        }
        // Double-submit CSRF on state-changing methods only (GET is exempt).
        if (!SAFE_METHODS.has(method)) {
            const csrf = typeof document === 'undefined'
                ? null
                : selectCsrfToken(document.cookie, config.getApiHost());
            if (csrf && !headers.has('X-CSRF-Token')) {
                headers.set('X-CSRF-Token', csrf);
            }
        }
        return headers;
    }
    async function apiFetch(path, options = {}) {
        const { skipRefresh, headers: initHeaders, ...rest } = options;
        const method = (rest.method ?? 'GET').toUpperCase();
        const url = `https://${config.getApiHost()}${path}`;
        const doFetch = () => fetch(url, {
            ...rest,
            method,
            credentials: 'include',
            headers: buildHeaders(method, initHeaders),
        });
        let response = await doFetch();
        if (response.status === 401 && !skipRefresh) {
            const refreshed = await runSingleFlightRefresh();
            if (refreshed) {
                response = await doFetch();
            }
        }
        if (response.status === 402) {
            config.onPaymentRequired?.();
        }
        return response;
    }
    async function apiFetchJson(path, options = {}) {
        const response = await apiFetch(path, options);
        const body = (await response.json().catch(() => null));
        if (!response.ok || !body || body.ok === false) {
            throw config.createError(response.status, body?.error ?? `request_failed_${response.status}`, body);
        }
        return body;
    }
    return {
        apiFetch,
        apiFetchJson,
        registerRefreshHandler: (handler) => {
            refreshImpl = handler;
        },
    };
}
